import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Upload, Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { api, getAdminToken, resolveImageUrl } from '../lib/api';

const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  title: '',
  slug: '',
  summary: '',
  content_html: '',
  featured_image: '',
  author: '',
  category: '',
  tags: '',
  date: today(),
  meta_title: '',
  meta_description: '',
  published: true,
  faqs: [],
});

const emptyErrors = () => ({
  title: '',
  author: '',
  date: '',
  category: '',
  tags: '',
  slug: '',
  summary: '',
  featured_image: '',
  content_html: '',
  meta_title: '',
  meta_description: '',
  faqs: '',
});

const IMAGE_EXTENSIONS = /\.(avif|gif|jpe?g|png|svg|webp)(\?.*)?$/i;
const IMAGE_DATA_URL = /^data:image\/(avif|gif|jpeg|jpg|png|svg\+xml|webp);base64,/i;
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const TEXT_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 .,&'():;!?/-]*$/;
const TAG_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 -]*$/;

const stripHtml = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const isImageUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.startsWith('/api/uploads/')) return true;
  if (IMAGE_DATA_URL.test(trimmed)) return true;
  try {
    const url = new URL(trimmed);
    return ['http:', 'https:'].includes(url.protocol) && IMAGE_EXTENSIONS.test(url.pathname);
  } catch (e) {
    return false;
  }
};

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState(emptyForm());
  const [fieldErrors, setFieldErrors] = useState(emptyErrors());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const featuredFileRef = useRef(null);

  useEffect(() => {
    document.title = isNew ? 'New Blog - Admin' : 'Edit Blog - Admin';
    if (!getAdminToken()) {
      navigate('/admin/login');
      return;
    }
    if (isNew) return;

    const load = async () => {
      try {
        const res = await api.get(`/blogs/${id}`);
        const b = res.data;
        setForm({
          title: b.title || '',
          slug: b.slug || '',
          summary: b.summary || '',
          content_html: b.content_html || '',
          featured_image: b.featured_image || '',
          author: b.author || '',
          category: b.category || '',
          tags: (b.tags || []).join(', '),
          date: (b.date || today()).slice(0, 10),
          meta_title: b.meta_title || '',
          meta_description: b.meta_description || '',
          published: !!b.published,
          faqs: Array.isArray(b.faqs) ? b.faqs.map((f) => ({ question: f.question || '', answer: f.answer || '' })) : [],
        });
      } catch (e) {
        setError('Failed to load blog.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew, navigate]);

  const validateField = (key, value, currentForm = form) => {
    const trimmed = typeof value === 'string' ? value.trim() : value;

    if (key === 'title') {
      if (!trimmed) return 'Title is required.';
      if (trimmed.length > 120) return 'Title must be 120 characters or fewer.';
      return '';
    }
    if (key === 'author') {
      if (!trimmed) return 'Author is required.';
      if (trimmed.length > 80) return 'Author must be 80 characters or fewer.';
      if (!TEXT_PATTERN.test(trimmed)) return 'Use letters, numbers, spaces, and basic punctuation.';
      return '';
    }
    if (key === 'date') {
      if (!trimmed) return 'Date is required.';
      if (Number.isNaN(new Date(trimmed).getTime())) return 'Enter a valid date.';
      return '';
    }
    if (key === 'category') {
      if (!trimmed) return '';
      if (trimmed.length > 60) return 'Category must be 60 characters or fewer.';
      if (!TEXT_PATTERN.test(trimmed)) return 'Use letters, numbers, spaces, and basic punctuation.';
      return '';
    }
    if (key === 'tags') {
      if (!trimmed) return '';
      const tags = trimmed.split(',').map((tag) => tag.trim()).filter(Boolean);
      if (tags.length > 12) return 'Use 12 tags or fewer.';
      if (tags.some((tag) => tag.length > 32 || !TAG_PATTERN.test(tag))) {
        return 'Tags can use letters, numbers, spaces, and hyphens only.';
      }
      return '';
    }
    if (key === 'slug') {
      if (!trimmed) return '';
      if (trimmed.length > 100) return 'Slug must be 100 characters or fewer.';
      if (!SLUG_PATTERN.test(trimmed)) return 'Use lowercase letters, numbers, and single hyphens only.';
      return '';
    }
    if (key === 'summary') {
      if (!trimmed) return 'Summary is required.';
      if (trimmed.length > 300) return 'Summary must be 300 characters or fewer.';
      return '';
    }
    if (key === 'featured_image') {
      if (!isImageUrl(trimmed)) return 'Enter an image URL ending in jpg, jpeg, png, webp, gif, avif, or svg.';
      if (trimmed.length > 1000) return 'Image URL is too long.';
      return '';
    }
    if (key === 'content_html') {
      if (!stripHtml(currentForm.content_html || value)) return 'Body is required.';
      return '';
    }
    if (key === 'meta_title') {
      if (trimmed && trimmed.length > 70) return 'Meta title should be 70 characters or fewer.';
      return '';
    }
    if (key === 'meta_description') {
      if (trimmed && trimmed.length > 160) return 'Meta description should be 160 characters or fewer.';
      return '';
    }
    return '';
  };

  const validateFaqs = (faqs) => {
    const cleaned = faqs
      .map((f) => ({ question: (f.question || '').trim(), answer: (f.answer || '').trim() }))
      .filter((f) => f.question || f.answer);
    if (cleaned.some((f) => !f.question || !f.answer)) return 'Each FAQ needs both a question and an answer.';
    if (cleaned.some((f) => f.question.length > 160)) return 'FAQ questions must be 160 characters or fewer.';
    if (cleaned.some((f) => f.answer.length > 500)) return 'FAQ answers must be 500 characters or fewer.';
    return '';
  };

  const validateForm = () => {
    const nextErrors = {
      ...emptyErrors(),
      title: validateField('title', form.title),
      author: validateField('author', form.author),
      date: validateField('date', form.date),
      category: validateField('category', form.category),
      tags: validateField('tags', form.tags),
      slug: validateField('slug', form.slug),
      summary: validateField('summary', form.summary),
      featured_image: validateField('featured_image', form.featured_image),
      content_html: validateField('content_html', form.content_html),
      meta_title: validateField('meta_title', form.meta_title),
      meta_description: validateField('meta_description', form.meta_description),
      faqs: validateFaqs(form.faqs),
    };
    setFieldErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const update = (key, value) => {
    const nextValue = key === 'slug' ? value.toLowerCase().replace(/\s+/g, '-') : value;
    setForm((f) => {
      const nextForm = { ...f, [key]: nextValue };
      setFieldErrors((current) => ({ ...current, [key]: validateField(key, nextValue, nextForm) }));
      return nextForm;
    });
  };

  const handleFeaturedFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFieldErrors((current) => ({ ...current, featured_image: 'Upload an image file only.' }));
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFieldErrors((current) => ({ ...current, featured_image: 'Image must be 5 MB or smaller.' }));
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      update('featured_image', res.data.url);
      setFieldErrors((current) => ({ ...current, featured_image: '' }));
    } catch (err) {
      setFieldErrors((current) => ({ ...current, featured_image: 'Upload failed. Please choose another image.' }));
    } finally {
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) {
      setError('Please fix the highlighted fields.');
      return;
    }
    // Validate FAQs: each entry must have both question + answer (or be removed).
    const cleanedFaqs = form.faqs
      .map((f) => ({ question: (f.question || '').trim(), answer: (f.answer || '').trim() }))
      .filter((f) => f.question || f.answer);
    const incomplete = cleanedFaqs.find((f) => !f.question || !f.answer);
    if (incomplete) {
      setError('Each FAQ needs both a question and an answer (or remove the entry).');
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      faqs: cleanedFaqs,
    };
    if (!form.slug.trim()) delete payload.slug;
    try {
      if (isNew) {
        await api.post('/admin/blogs', payload);
      } else {
        await api.put(`/admin/blogs/${id}`, payload);
      }
      navigate('/admin/blogs');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ----- FAQ helpers -----
  const addFaq = () => setForm((f) => ({ ...f, faqs: [...f.faqs, { question: '', answer: '' }] }));
  const removeFaq = (idx) =>
    setForm((f) => {
      const nextFaqs = f.faqs.filter((_, i) => i !== idx);
      setFieldErrors((current) => ({ ...current, faqs: validateFaqs(nextFaqs) }));
      return { ...f, faqs: nextFaqs };
    });
  const updateFaq = (idx, key, value) =>
    setForm((f) => {
      const nextFaqs = f.faqs.map((it, i) => (i === idx ? { ...it, [key]: value } : it));
      setFieldErrors((current) => ({ ...current, faqs: validateFaqs(nextFaqs) }));
      return { ...f, faqs: nextFaqs };
    });
  const moveFaq = (idx, dir) =>
    setForm((f) => {
      const next = [...f.faqs];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return f;
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...f, faqs: next };
    });

  if (loading) {
    return <div className="px-6 py-10 max-w-4xl mx-auto">Loading...</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to="/admin/blogs" className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-brand-blue">
            <ArrowLeft size={16} /> Back to all blogs
          </Link>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{isNew ? 'New Blog' : 'Edit Blog'}</h1>
        <p className="text-sm text-gray-600 mb-8">Fill in the fields below and click Publish.</p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-blog-form">
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="admin-form-error">
              {error}
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                data-testid="admin-form-title"
                maxLength={120}
                aria-invalid={Boolean(fieldErrors.title)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  fieldErrors.title ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                }`}
                required
              />
              {fieldErrors.title && <p className="mt-1 text-xs text-red-600">{fieldErrors.title}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Author *</label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => update('author', e.target.value)}
                  data-testid="admin-form-author"
                  maxLength={80}
                  autoComplete="name"
                  aria-invalid={Boolean(fieldErrors.author)}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    fieldErrors.author ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                  }`}
                  required
                />
                {fieldErrors.author && <p className="mt-1 text-xs text-red-600">{fieldErrors.author}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => update('date', e.target.value)}
                  data-testid="admin-form-date"
                  aria-invalid={Boolean(fieldErrors.date)}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    fieldErrors.date ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                  }`}
                  required
                />
                {fieldErrors.date && <p className="mt-1 text-xs text-red-600">{fieldErrors.date}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => update('category', e.target.value)}
                  data-testid="admin-form-category"
                  placeholder="e.g. Safai Saathis"
                  maxLength={60}
                  aria-invalid={Boolean(fieldErrors.category)}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    fieldErrors.category ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                  }`}
                />
                {fieldErrors.category && <p className="mt-1 text-xs text-red-600">{fieldErrors.category}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={(e) => update('tags', e.target.value)}
                  data-testid="admin-form-tags"
                  placeholder="e.g. circular-economy, esg"
                  maxLength={240}
                  aria-invalid={Boolean(fieldErrors.tags)}
                  className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    fieldErrors.tags ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                  }`}
                />
                {fieldErrors.tags && <p className="mt-1 text-xs text-red-600">{fieldErrors.tags}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL slug (optional)</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => update('slug', e.target.value)}
                data-testid="admin-form-slug"
                placeholder="auto-generated from title if empty"
                maxLength={100}
                inputMode="url"
                aria-invalid={Boolean(fieldErrors.slug)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  fieldErrors.slug ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                }`}
              />
              {fieldErrors.slug && <p className="mt-1 text-xs text-red-600">{fieldErrors.slug}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Short summary *</label>
              <textarea
                value={form.summary}
                onChange={(e) => update('summary', e.target.value)}
                rows={3}
                data-testid="admin-form-summary"
                maxLength={300}
                aria-invalid={Boolean(fieldErrors.summary)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  fieldErrors.summary ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                }`}
                required
              />
              {fieldErrors.summary && <p className="mt-1 text-xs text-red-600">{fieldErrors.summary}</p>}
              <p className="text-xs text-gray-500 mt-1">Shown on blog cards. 2-3 lines recommended.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Featured image</label>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
                <div className="flex-1">
                  <input
                    type="text"
                    value={form.featured_image}
                    onChange={(e) => update('featured_image', e.target.value)}
                    data-testid="admin-form-featured-image"
                    placeholder="Paste an image URL or upload below"
                    inputMode="url"
                    aria-invalid={Boolean(fieldErrors.featured_image)}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      fieldErrors.featured_image ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                    }`}
                  />
                  {fieldErrors.featured_image && <p className="mt-1 text-xs text-red-600">{fieldErrors.featured_image}</p>}
                  <button
                    type="button"
                    onClick={() => featuredFileRef.current?.click()}
                    className="mt-2 inline-flex items-center gap-2 text-sm text-brand-blue hover:underline"
                    data-testid="admin-form-featured-upload"
                  >
                    <Upload size={14} /> Upload image
                  </button>
                  <input
                    ref={featuredFileRef}
                    type="file"
                    accept="image/avif,image/gif,image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleFeaturedFile}
                  />
                </div>
                {form.featured_image && (
                  <img
                    src={resolveImageUrl(form.featured_image)}
                    alt="Featured preview"
                    className="w-40 h-28 object-cover rounded-md border border-gray-200"
                    data-testid="admin-form-featured-preview"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Body *</label>
            <RichTextEditor
              value={form.content_html}
              onChange={(html) => update('content_html', html)}
            />
            {fieldErrors.content_html && <p className="mt-2 text-xs text-red-600">{fieldErrors.content_html}</p>}
          </div>

          {/* Frequently Asked Questions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4" data-testid="admin-form-faqs">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-900">Frequently Asked Questions (FAQs)</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Optional. Add Q&amp;A pairs that complement the article. They are stored with the post and exposed via the API for future on-page rendering and rich-result SEO.
                </p>
              </div>
              <button
                type="button"
                onClick={addFaq}
                data-testid="admin-form-faq-add"
                className="inline-flex shrink-0 items-center gap-2 px-3 py-2 bg-brand-blue text-white rounded-md hover:opacity-90 text-sm"
              >
                <Plus size={14} /> Add FAQ
              </button>
            </div>
            {fieldErrors.faqs && <p className="text-xs text-red-600">{fieldErrors.faqs}</p>}

            {form.faqs.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                No FAQs yet. Click <span className="font-medium text-gray-700">"Add FAQ"</span> to create your first one.
              </div>
            ) : (
              <div className="space-y-4">
                {form.faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    data-testid={`admin-form-faq-item-${idx}`}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-semibold">
                        FAQ #{idx + 1}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveFaq(idx, -1)}
                          disabled={idx === 0}
                          title="Move up"
                          className="p-1.5 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`admin-form-faq-up-${idx}`}
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveFaq(idx, 1)}
                          disabled={idx === form.faqs.length - 1}
                          title="Move down"
                          className="p-1.5 rounded hover:bg-gray-200 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`admin-form-faq-down-${idx}`}
                        >
                          <ChevronDown size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFaq(idx)}
                          title="Remove this FAQ"
                          className="p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600"
                          data-testid={`admin-form-faq-remove-${idx}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Question
                    </label>
                    <input
                      type="text"
                      value={faq.question}
                      onChange={(e) => updateFaq(idx, 'question', e.target.value)}
                      placeholder="e.g. How does ReCircle measure impact?"
                      data-testid={`admin-form-faq-question-${idx}`}
                      maxLength={160}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue mb-3"
                    />
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Answer
                    </label>
                    <textarea
                      value={faq.answer}
                      onChange={(e) => updateFaq(idx, 'answer', e.target.value)}
                      placeholder="Write a clear, concise answer."
                      rows={3}
                      data-testid={`admin-form-faq-answer-${idx}`}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue resize-y"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">SEO</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta title</label>
              <input
                type="text"
                value={form.meta_title}
                onChange={(e) => update('meta_title', e.target.value)}
                data-testid="admin-form-meta-title"
                maxLength={70}
                aria-invalid={Boolean(fieldErrors.meta_title)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  fieldErrors.meta_title ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                }`}
              />
              {fieldErrors.meta_title && <p className="mt-1 text-xs text-red-600">{fieldErrors.meta_title}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta description</label>
              <textarea
                value={form.meta_description}
                onChange={(e) => update('meta_description', e.target.value)}
                rows={2}
                data-testid="admin-form-meta-description"
                maxLength={160}
                aria-invalid={Boolean(fieldErrors.meta_description)}
                className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  fieldErrors.meta_description ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:ring-brand-blue'
                }`}
              />
              {fieldErrors.meta_description && <p className="mt-1 text-xs text-red-600">{fieldErrors.meta_description}</p>}
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => update('published', e.target.checked)}
                data-testid="admin-form-published"
                className="rounded border-gray-300"
              />
              Published (visible on the site)
            </label>
          </div>

          <div className="flex justify-end gap-3">
            <Link
              to="/admin/blogs"
              className="px-5 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              data-testid="admin-form-submit"
              className="inline-flex items-center gap-2 px-5 py-2 bg-brand-blue text-white rounded-md hover:opacity-90 disabled:opacity-60"
            >
              <Save size={16} /> {saving ? 'Saving...' : isNew ? 'Publish blog' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminBlogEditor;
